import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import { getVisibleSelectionRect } from "draft-js";

import Button from "components/library/Button";
import Popover from "components/library/Popover";
import TextInput from "components/library/TextInput";
import shared from "../../../shared.css";

import getActiveEditorState from "editor/utils/getActiveEditorState";
import getSelectedBlockElement from "editor/utils/getSelectedBlockElement";
import { EDITOR_MODALS } from "config/constants/editor";
import { getOembedAsync } from "editor/actions/embed";
import { toggleModal } from "editor/actions/ui";

import styles from "./EmbedEditor.css";

class EmbedEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = { url: this.props.url };

    this.closeModal = this._closeModal.bind(this);
    this.handleOnChange = this._handleOnChange.bind(this);
    this.handleOnClick = this._handleOnClick.bind(this);
  }

  UNSAFE_componentWillReceiveProps() {
    this.setState({ url: this.props.url });
  }

  calculateOffset() {
    if (this.props.openModal !== EDITOR_MODALS.EMBED_EDITOR) {
      return null;
    }

    // Regardless if there's an anchor defined from a selection, we want to use
    // the left position of the block to align the popover.
    const blockElement = getSelectedBlockElement();

    if (!blockElement) {
      return null;
    }

    const blockRect = blockElement.getBoundingClientRect();
    const selectionRect = getVisibleSelectionRect(window);
    let anchor;

    if (selectionRect) {
      anchor = selectionRect;
      anchor.left = blockRect.left;

      // anchor to an empty content block (paragraph)
    } else {
      anchor = blockRect;
    }

    let { top, left } = anchor;

    // TODO: Optimize query selector by getting height from a ref instead.
    const editorElement = document.querySelector(".embed-editor");
    const editorHeight = editorElement ? editorElement.clientHeight : 170;

    const spacing = parseFloat(shared["scale-3"]) * 10;

    // a buffer of spacing around the edge of the window so the popover will
    // always be positioned at least BUFFER inside the window
    const BUFFER = spacing;

    top -= spacing;

    // check bottom bound and adjust if necessary
    if (top + editorHeight - window.innerHeight > BUFFER) {
      top = window.innerHeight - (editorHeight + BUFFER);
    }

    return { top, left };
  }

  _handleOnClick(e) {
    e.preventDefault();
    this.props.getOembedAsync(this.state.url.trim());
  }

  _handleOnChange(e) {
    this.setState({ [e.target.name]: e.target.value });
  }

  _closeModal() {
    this.props.toggleModal(EDITOR_MODALS.CLOSE);
  }

  isOpened() {
    return this.props.openModal === EDITOR_MODALS.EMBED_EDITOR;
  }

  render() {
    let error = null;
    let embedErr = null;

    if (this.props.embedContext) {
      embedErr = this.props.embedContext;

      if (embedErr.status === 404) {
        // We're mimicking an error object that the TextInput expects here.
        error = {
          message: (
            <div>
              Please insert a{" "}
              <a
                href="https://docs.google.com/document/d/1K8kAKEXOdCdUVnhAv3f-jOFg6Epci5XtpUiS0FAEukA"
                target="_blank"
              >
                supported
              </a>{" "}
              embed.
            </div>
          ),
          title: "Incorrect URL",
        };
      } else if (embedErr.status === 424) {
        error = embedErr.decodedJSON.detail;
      }
    }

    return (
      <Popover
        className={classNames("embed-editor", styles.root)}
        closeModal={this.closeModal}
        isOpened={this.isOpened()}
        position={this.calculateOffset()}
      >
        <form>
          <TextInput
            autoFocus
            className="gtm-embed-media-field"
            error={error}
            label="URL"
            name="url"
            onChange={this.handleOnChange}
            placeholder="Type a URL"
            type="text"
            value={this.state.url}
          />
          <Button
            className="gtm-embed-media"
            fullWidth
            isDisabled={!this.state.url}
            onClick={this.handleOnClick}
            type="primary"
          >
            Embed
          </Button>
        </form>
      </Popover>
    );
  }
}

const mapStateToProps = state => {
  return {
    openModal: state.editor.ui.openModal,
    editorState: getActiveEditorState(state),
    embedContext: state.editor.embed.context,
  };
};

const mapDispatchToProps = {
  getOembedAsync,
  toggleModal,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EmbedEditor);

EmbedEditor.propTypes = {
  editorState: PropTypes.object.isRequired,
  entityKey: PropTypes.string,
  id: PropTypes.string,
  openModal: PropTypes.oneOf(Object.values(EDITOR_MODALS)),
  toggleModal: PropTypes.func.isRequired,
  getOembedAsync: PropTypes.func,
  url: PropTypes.string,
};
